/**
 * useCountdown - Live countdown timer hook
 *
 * Provides real-time countdown to a target date/time with automatic updates.
 *
 * Features:
 * - Updates every second for live countdown
 * - Returns hours, minutes, seconds breakdown
 * - Indicates when target time has passed (isExpired)
 * - Handles null/invalid dates gracefully
 * - Auto-cleanup on unmount
 *
 * Usage:
 * ```typescript
 * const countdown = useCountdown('2025-11-08T00:00:00Z');
 * console.log(`${countdown.hours}h ${countdown.minutes}m ${countdown.seconds}s`);
 * if (countdown.isExpired) {
 *   console.log('Time has passed!');
 * }
 * ```
 */
import { useState, useEffect } from 'react';

export interface CountdownTime {
  /** Days remaining */
  days: number;
  /** Hours remaining (0-23) */
  hours: number;
  /** Minutes remaining (0-59) */
  minutes: number;
  /** Seconds remaining (0-59) */
  seconds: number;
  /** Total milliseconds remaining */
  totalMs: number;
  /** True if target time has passed */
  isExpired: boolean;
}

/**
 * Calculate time remaining until target date
 */
function calculateTimeRemaining(targetDate: string | null): CountdownTime {
  if (!targetDate) {
    return {
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0,
      totalMs: 0,
      isExpired: true,
    };
  }

  const now = Date.now();
  const target = new Date(targetDate).getTime();
  const remaining = Math.max(0, target - now);

  const days = Math.floor(remaining / 86400000); // 24 hours in ms
  const hours = Math.floor((remaining % 86400000) / 3600000);
  const minutes = Math.floor((remaining % 3600000) / 60000);
  const seconds = Math.floor((remaining % 60000) / 1000);

  return {
    days,
    hours,
    minutes,
    seconds,
    totalMs: remaining,
    isExpired: remaining === 0,
  };
}

/**
 * Hook to provide live countdown timer
 *
 * @param targetDate - ISO 8601 date string (e.g., '2025-11-08T00:00:00Z')
 * @returns CountdownTime object with hours, minutes, seconds, and isExpired flag
 *
 * @example
 * ```typescript
 * function MyComponent() {
 *   const countdown = useCountdown('2025-11-08T00:00:00Z');
 *
 *   return (
 *     <Text>
 *       {countdown.isExpired
 *         ? 'Time has passed!'
 *         : `${countdown.hours}h ${countdown.minutes}m ${countdown.seconds}s`
 *       }
 *     </Text>
 *   );
 * }
 * ```
 */
export function useCountdown(targetDate: string | null): CountdownTime {
  const [countdown, setCountdown] = useState<CountdownTime>(() =>
    calculateTimeRemaining(targetDate)
  );

  useEffect(() => {
    if (!targetDate) {
      setCountdown({
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
        totalMs: 0,
        isExpired: true,
      });
      return;
    }

    // Update immediately on mount or targetDate change
    setCountdown(calculateTimeRemaining(targetDate));

    // Update every second
    const intervalId = setInterval(() => {
      const newCountdown = calculateTimeRemaining(targetDate);
      setCountdown(newCountdown);

      // Stop interval if expired (optimization)
      if (newCountdown.isExpired) {
        clearInterval(intervalId);
      }
    }, 1000) as unknown as number;

    // Cleanup on unmount or targetDate change
    return () => clearInterval(intervalId);
  }, [targetDate]);

  return countdown;
}
